import { useState, useEffect, useMemo, useCallback } from 'react';

import { useReadTime } from '@confluence/read-time';
import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_INSIGHTS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { fg } from '@confluence/feature-gating';

const ONBOARDING_DELAY_MS = 3000;
const MIN_READ_TIME_MINUTES = 3;

export const useAudioBriefingChangeboarding = ({
	contentId,
	onShow,
	skip,
}: {
	contentId: string;
	onShow: () => void;
	skip: boolean;
}) => {
	const [showChangeboarding, setShowChangeboarding] = useState(false);

	const { readTime } = useReadTime(contentId);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(ONBOARDING_INSIGHTS.AUDIO_BRIEFING);

	const { setOnboardingState } = useSetOnboardingState();
	const { hasAudioBriefingChangeboardingBeenSeen } = deserializeState(onboardingStateData);

	const shouldShowChangeboarding = useMemo(() => {
		const areConditionsMet =
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('confluence_audio_briefing_changeboarding') &&
			!skip &&
			readTime &&
			readTime >= MIN_READ_TIME_MINUTES &&
			!onboardingStateLoading &&
			!onboardingStateError &&
			!hasAudioBriefingChangeboardingBeenSeen;
		return !!areConditionsMet;
	}, [
		readTime,
		onboardingStateLoading,
		onboardingStateError,
		hasAudioBriefingChangeboardingBeenSeen,
		skip,
	]);

	// The effect that auto opens the popper
	useEffect(() => {
		// Always reset spotlight on content change or if conditions change
		setShowChangeboarding(false);

		if (shouldShowChangeboarding) {
			const timer = setTimeout(() => {
				setShowChangeboarding(true);
				onShow();
			}, ONBOARDING_DELAY_MS);
			return () => clearTimeout(timer);
		}
	}, [contentId, shouldShowChangeboarding, onShow]);

	const handleCloseChangeboarding = useCallback(() => {
		setOnboardingState({
			key: ONBOARDING_INSIGHTS.AUDIO_BRIEFING,
			value: 'true',
		});
		setShowChangeboarding(false);
	}, [setOnboardingState]);

	return { showChangeboarding, handleCloseChangeboarding };
};
